/**
 * One version of a judge prompt, picked in the tree of the Judge prompts page, with its
 * actions. A new version starts from this one and is saved next to it; nothing is ever
 * overwritten.
 */
import { Bot, CircleCheck, Pencil } from 'lucide-react';
import { useState } from 'react';
import Button from '../../../../components/Button';
import CopyButton from '../../../../components/CopyButton';
import InlineErrorDisplay from '../../../../components/InlineErrorDisplay';
import type { JudgePromptInfo, JudgePromptVersion } from '../../types/evalTypes';
import { formatDateTime, versionAuthor } from '../../utils/evalFormat';
import { VersionArchiveControl } from '../VersionArchiveControl';

const MAX_PROMPT_CHARS = 50_000;
const MAX_NOTE_CHARS = 2000;

interface JudgePromptCardProps {
  judge: JudgePromptInfo;
  /** Undefined only while the judge has no versions: then the card asks for v1. */
  version: JudgePromptVersion | undefined;
  currentUid: string | undefined;
  /** All throw on failure. */
  onCreate: (version: { text: string; note: string; makeCurrent: boolean }) => Promise<void>;
  onMakeCurrent: (versionId: string) => Promise<void>;
  /** Archives (with a reason) or restores one version. */
  onSetVersionArchived: (versionId: string, archived: boolean, reason?: string) => Promise<void>;
}

export const JudgePromptCard = ({ judge, version, currentUid, onCreate, onMakeCurrent, onSetVersionArchived }: JudgePromptCardProps) => {
  // A judge with no versions has no prompt yet, so the only thing to do is write v1.
  const [editing, setEditing] = useState(version === undefined);
  const [text, setText] = useState('');
  const [note, setNote] = useState('');
  const [makeCurrent, setMakeCurrent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isFirst = judge.versions.length === 0;
  const isCurrent = version?.versionId === judge.currentVersionId;

  const run = async (action: () => Promise<void>, fallback: string) => {
    setSaving(true);
    setError(null);
    try {
      await action();
      return true;
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : fallback);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const startEditing = () => {
    setText(version?.text ?? '');
    setNote('');
    setMakeCurrent(false);
    setError(null);
    setEditing(true);
  };

  const save = async () => {
    // The page selects the new version once it is saved.
    if (await run(() => onCreate({ text, note: note.trim(), makeCurrent }), 'Failed to save the version.')) {
      setEditing(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 space-y-3 min-w-0">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-gray-900">
            <span className="font-mono">{judge.judgeName}</span>
            {version && <span className="ml-2">v{version.number}</span>}
            {isCurrent && <span className="ml-2 align-middle text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-800">current</span>}
            {version?.archived && <span className="ml-2 align-middle text-[10px] px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-700">archived</span>}
          </h2>
          <p className="text-xs text-gray-600">
            <span className="font-mono">{judge.suiteId}</span>
            {version && (
              <>
                {' '}
                · {formatDateTime(version.createdAt)}, {versionAuthor(version, currentUid)}
                {version.tokenName !== undefined && <Bot size={12} className="inline ml-1 text-violet-700" aria-label="Proposed by Claude" />} ·{' '}
                <span className="font-mono">{version.hash}</span>
              </>
            )}
          </p>
          {version?.note && <p className="text-sm text-gray-800 mt-1">{version.note}</p>}
        </div>
        {version && !editing && (
          <div className="flex flex-wrap justify-end gap-2">
            <CopyButton text={version.text} label="Copy" />
            {!version.archived && (
              <VersionArchiveControl
                archived={undefined}
                isCurrent={isCurrent}
                currentUid={currentUid}
                onArchive={(reason) => onSetVersionArchived(version.versionId, true, reason)}
                onRestore={() => onSetVersionArchived(version.versionId, false)}
              />
            )}
            {!isCurrent && !version.archived && (
              <Button
                variant="outline"
                size="sm"
                icon={<CircleCheck size={13} />}
                onClick={() => void run(() => onMakeCurrent(version.versionId), 'Failed to make it current.')}
                disabled={saving}
                loading={saving}
              >
                Make current
              </Button>
            )}
            <Button variant="primary" size="sm" icon={<Pencil size={13} />} onClick={startEditing} disabled={saving}>
              New version from this
            </Button>
          </div>
        )}
      </div>

      {editing ? (
        <div className="space-y-2">
          <p className="text-sm text-gray-600">
            {version ? `New version, starting from v${version.number}.` : 'v1. It becomes current at once.'} Keep the structure the judge's
            output needs: the code reads its verdicts.
          </p>
          <textarea
            value={text}
            maxLength={MAX_PROMPT_CHARS}
            onChange={(event) => setText(event.target.value)}
            disabled={saving}
            rows={24}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs font-mono text-gray-900 resize-y"
          />
          <input
            value={note}
            maxLength={MAX_NOTE_CHARS}
            onChange={(event) => setNote(event.target.value)}
            disabled={saving}
            placeholder="What changed, and why? For example: fact checks now accept paraphrases of numbers."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm text-gray-900"
          />
          {!isFirst && (
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={makeCurrent} disabled={saving} onChange={(event) => setMakeCurrent(event.target.checked)} />
              Make it current: runs queued from now on use it
            </label>
          )}
          {error && <InlineErrorDisplay message={error} onDismiss={() => setError(null)} />}
          <div className="flex justify-end gap-2">
            {!isFirst && (
              <Button variant="outline" size="sm" onClick={() => setEditing(false)} disabled={saving}>
                Cancel
              </Button>
            )}
            <Button variant="primary" size="sm" onClick={() => void save()} disabled={saving || text.trim() === '' || text === version?.text} loading={saving}>
              {isFirst ? 'Save v1' : 'Save version'}
            </Button>
          </div>
        </div>
      ) : (
        version && (
          <>
            {error && <InlineErrorDisplay message={error} onDismiss={() => setError(null)} />}
            {version.archived && (
              <VersionArchiveControl
                archived={version.archived}
                isCurrent={false}
                currentUid={currentUid}
                onArchive={(reason) => onSetVersionArchived(version.versionId, true, reason)}
                onRestore={() => onSetVersionArchived(version.versionId, false)}
              />
            )}
            <pre className="text-xs text-gray-900 whitespace-pre-wrap bg-gray-50 rounded-lg p-3 max-h-[48rem] overflow-y-auto">{version.text}</pre>
          </>
        )
      )}
    </div>
  );
};
